import React, { useState, useEffect, useMemo } from 'react';
import { doc, getDoc, addDoc, updateDoc, collection, deleteDoc, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { auth } from '../firebase';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useRole } from '../hooks/useRole';
import { useData } from '../context/DataContext';
import {
  getPaymentStatus, isSettled, PAYMENT_STATUS, paymentMirrorFields,
  fetchInvoicePayments, syncInvoicePaymentMirror, mirrorSyncFailedMessage,
} from '../utils/paymentStatus';
import { generateDocNumber } from '../utils/docNumber';
import { convertQuoteToInvoice } from '../utils/convertQuote';
import { lineTotal, round2 } from '../utils/lineMath';
import { getDeductableQuantities, computeInventoryDelta, describeDelta, applyInventoryDelta, buildUpdatedLedger, getEffectiveLedger, resolveInventoryStatus } from '../utils/inventoryLedger';
import {
  isPickRequested, resolvePickStatus, PICK_STATUS, pickRequestFields, pickCancelFields,
  isItemPicked, toggleItemFields, toggledItemIds, backOrderCount, collectableCount,
  collectableItems, itemKey,
} from '../utils/pickStatus';
// Display only. buildSignatureFields and the pad itself are NOT imported here
// any more: the office does not capture signatures, so it does not need the
// tools for it. SignaturePad.js is otherwise untouched — the shop screen still
// uses all of it.
import { hasSignature, SignatureView } from '../components/SignaturePad';
import { BOARD_STATUS_OPTIONS, DEFAULT_BOARD_STATUS } from '../utils/boardColumns';
import CreditHoldWarning from '../components/CreditHoldWarning';

// Money string formatter: negatives render as "-$24.94" (minus before the sign),
// not "$-24.94". Formatting only — never changes the underlying value.
const formatMoney = (n) => (n < 0 ? `-$${Math.abs(n).toFixed(2)}` : `$${n.toFixed(2)}`);
// 4-decimal variant for the line-item Unit Price column ONLY — price-list values
// carry 4 decimals, so 2-decimal display made "$0.33" look wrong. The per-line
// Total and all summary totals (Subtotal/Tax/Delivery/Grand Total) stay on
// formatMoney (2 decimals — the money actually charged).
const formatMoney4 = (n) => (n < 0 ? `-$${Math.abs(n).toFixed(4)}` : `$${n.toFixed(4)}`);

const TERMS = [
  "35% handling charge on all returns; management approval required.",
  "We may refuse any return for any reason.",
  "No returns on non-stock items, hardware, wood, or concrete.",
  "Quotes valid 10 days from date issued.",
  "Items unclaimed 14+ days: $5/day storage fee.",
  "Items unclaimed 30+ days returned to stock or disposed, no refund."
];

// Trailing zeros dropped, two decimals kept — a quarter inch has to survive,
// and this must read the same as the drawing's own labels do.
const drawingSize = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `${Math.round(n * 100) / 100}"` : '—';
};

// ── THE PRINTED SIGNATURE SLOT ───────────────────────────────────────
//
// ONE slot, in the SAME footprint the sheet had before signature capture
// existed. Signed or not, it occupies the same height: unsigned it is a blank
// rule with a space above it to sign in, signed it is that same space with the
// signature drawn in it. Never both, never two.
//
// WHAT THIS REPLACED. PrintSignatureBlock was rendering its own heading plus
// THREE ruled sub-lines (Signature / Print name / Date) with 30px of margin
// above them, on top of the sheet's existing rule. The packing slip ended up
// with the dispatcher's line AND a second labelled block; the invoice went from
// one rule to three. That component is still exactly right for the shop's
// picking list, which has nothing else on that part of the page — so it is left
// alone and the office simply stops using it.
//
// Deliberately local to this file. Changing PrintSignatureBlock would have
// changed the shop screen too, and the shop screen is not to be touched.
const fmtSignedAt = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US');
};

// Greyed and unclickable at the ends rather than removed — see the note where
// these are rendered.
const navStepStyle = (disabled) => ({
  background: disabled ? '#F1F4F8' : '#0077B6',
  border: '1px solid ' + (disabled ? '#E3ECF5' : '#0077B6'),
  color: disabled ? '#9AA8BC' : '#FFFFFF',
  borderRadius: 8, padding: '6px 14px',
  fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
  cursor: disabled ? 'not-allowed' : 'pointer',
});

// ── THE NON-STOCK BADGE ON PAPER ─────────────────────────────────────
//
// PRINT ONLY. On screen the badge is unchanged: a wrapped row on a monitor
// costs nothing, and the on-screen size is the readable one. On paper a
// wrapped row costs a line, and enough of them push a packing slip onto a
// second sheet. So the compact treatment lives in @media print, where the
// cost is real, and nowhere else — screen and print deliberately differ.
//
// WHAT WAS WRAPPING, AND WHY — MEASURED, not guessed. Laid out in headless
// Edge at print width, a plain row is 24px and EVERY non-stock row was 44px,
// including one whose description is 20 characters long with 400px of
// Description column to spare. So it was never the description running out of
// room. A non-stock row carries the word NON-STOCK three times — the
// placeholder SKU in the SKU column, the inline badge after the description,
// and the pill in the Stock column — and the hyphen in that word is a legal
// line-break. The table's auto layout squeezes the narrow columns toward the
// width of their shortest unbreakable piece, which for a hyphenated word is
// the part before the hyphen; the Stock column (95px) came out narrower than
// its pill (~83px), and the pill broke into "NON-" / "STOCK". That cell's <td>
// has no text-xs, so its lines are 20px: two of them plus padding is the 44px.
// With the pill held together, the SKU column (74px) did the same to its own
// NON-STOCK at 16px lines — 36px rows — so it is held together too.
//
// The fix, then, is first NO-WRAP on all three, and only second size: the
// smaller badges give the Description column back ~16px and keep the badge
// from being the thing that tips a long description onto a second line.
//
// ONE OF THE SLIP'S TWO COPIES IS HIDDEN ON PAPER. With every word held
// together, the last thing tipping a row over was the inline badge on the
// longest real descriptions — 64 characters fits the column, 64 plus the badge
// does not. On the packing slip that badge is a duplicate: the Stock column
// on the same row already says NON-STOCK, boxed, for exactly the rows that
// carry it (isNonStockCustom is a subset of !isStock). So in print the slip
// keeps the column and drops the inline copy, and every real description
// measured fits on one line. The invoice copy has no Stock column, so its
// inline badge stays — shrunk, never hidden.
//
// LEGIBLE IN BLACK AND WHITE. Backgrounds are dropped by most printers, and
// orange text prints as a mid grey. The word itself is the signal, not the
// colour — bold capitals at 8.5px — and NON-STOCK is additionally boxed with
// a 1px border, which prints on any printer. STOCK is left unboxed, so on a
// black-and-white sheet the boxed one is the exception, which is the point.
//
// THE WORD STAYS "NON-STOCK". "NS" or "N/S" would save about 30px but reads
// as "not specified" or "no sale" to anyone who does not already know the
// code, and the person reading this is standing in a warehouse deciding
// whether to look for the item on a shelf. The saving comes from size.
//
// !important throughout: the inline badges carry inline styles, and the
// pills carry Tailwind classes loaded ahead of this block; both would
// otherwise win.
const PRINT_BADGE_CSS = `
  @media print {
    .ns-inline {
      font-size: 8.5px !important;
      font-style: normal !important;
      font-weight: 800 !important;
      letter-spacing: 0.3px;
      line-height: 1 !important;
      white-space: nowrap;
      margin-left: 4px !important;
      padding: 0 3px;
      border: 1px solid #854F0B;
      border-radius: 3px;
    }
    .stock-pill {
      font-size: 8.5px !important;
      line-height: 1 !important;
      padding: 1px 4px !important;
      letter-spacing: 0.3px;
      white-space: nowrap;
    }
    .stock-pill-ns { border: 1px solid #854F0B; }
    /* The slip's inline copy: the Stock column beside it says the same
       thing, boxed. See the note above. */
    .ns-inline-slip { display: none !important; }
    /* The SKU column. A non-stock item's SKU is the literal placeholder
       NON-STOCK, and a SKU broken across two lines is unreadable whatever it
       says — a real SKU with a hyphen in it would break the same way. */
    .slip-sku { white-space: nowrap; }
  }
`;

// ── THE ACTION COLUMN AND THE DOCUMENT, SHARING THE SCREEN ────────────
//
// The action column is fixed to the window's right edge, so it stays in view
// while a long invoice scrolls. The document and the panels under it are a
// centred column up to 896px wide. A fixed element takes up no room in the
// page, so on anything narrower than ~1370px the two used to collide and the
// buttons sat on top of the document's right edge.
//
// The column stays fixed — the buttons are used constantly, and a fixed column
// never has to be hunted for. What changes is that the CONTENT now reserves the
// column's room on the right, but only once it needs to:
//
//   padding-right: max(16px, 1372px - 100%)
//
// 1372 = 16px left gutter + 896px document + 2 × 230px, where 230 is what must
// stay clear on the right (24px margin + 190px of buttons + 16px gap). It is
// doubled because the document is centred: every pixel of padding moves its
// centre by only half a pixel. On a wide screen the padding is 16px on both
// sides, so the document sits exactly where it always did; at 1280 it slides
// left just far enough to clear the buttons. It never shrinks at laptop
// widths — at 1280 there is room to spare.
//
// The column also scrolls by itself when it is taller than the window. It is
// fixed, so the page scrolling could never reach a button that fell below the
// bottom edge — and on a laptop-height window, some did.
//
// Below 1100px wide there is no longer room for both side by side, so the
// column joins the page ABOVE the document as a wrapped row of the same buttons
// (flex order puts it there without moving it in the markup).
//
// SCREEN ONLY. None of this applies in print: the column is hidden there
// already, and the printed document keeps the layout it has always had.
const ACTIONS_LAYOUT_CSS = `
  @media screen {
    .vd-actions {
      position: fixed;
      top: 90px;
      right: 21px;
      width: 196px;
      box-sizing: border-box;
      padding: 3px 3px 12px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      z-index: 20;
      max-height: calc(100vh - 90px);
      overflow-y: auto;
      overscroll-behavior: contain;
    }
    .vd-content {
      padding-left: 16px;
      padding-right: max(16px, calc(1372px - 100%));
    }
  }
  @media screen and (max-width: 1099px) {
    .vd-root { display: flex; flex-direction: column; }
    .vd-actions {
      order: 1;
      position: static;
      width: auto;
      max-height: none;
      overflow: visible;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
      margin: 16px 16px 0;
      padding: 10px 12px;
      background: #FFFFFF;
      border: 1px solid #E3ECF5;
      border-radius: 12px;
    }
    .vd-actions > * { width: auto !important; }
    .vd-actions > p { margin: 0 0 0 8px !important; }
    .vd-content { order: 2; padding-right: 16px; }
  }
`;

const METHOD_ICON = { 'Cash': '💵', 'Credit/Debit Card': '💳', 'Check': '🏦', 'Other': '🔄' };


function ViewDocument({ user }) {
  const navigate = useNavigate();
  const { type, id } = useParams();
  const location = useLocation();

  // ── STEPPING THROUGH A LIST ──────────────────────────────────────────
  //
  // Present only when the user arrived from a list that handed one over — the
  // customer profile's Quotes or Invoices tab. Opening the same document from
  // the Documents page, a search result or a pasted link carries no state and
  // renders no chrome at all: there is no list to step through, and inventing
  // one would be guessing at an order the user never chose.
  //
  // The ids are a SNAPSHOT taken when they left the profile. Nothing refreshes
  // it, so a document deleted in the meantime is still in the array — see
  // stepTo below for what happens then.
  const docNav = location.state?.docNav || null;
  const navIds = Array.isArray(docNav?.ids) ? docNav.ids : [];
  const navIndex = Number.isInteger(docNav?.index) ? docNav.index : -1;
  // The index is trusted only if it still points at the document on screen. A
  // stale one would number the position wrongly and step from the wrong place.
  const navPos = navIds[navIndex] === id ? navIndex : navIds.indexOf(id);
  const showDocNav = !!docNav && navIds.length > 1 && navPos >= 0;
  const hasPrev = showDocNav && navPos > 0;
  const hasNext = showDocNav && navPos < navIds.length - 1;

  // Move one document along, carrying the SAME list with us so the user can
  // keep going. The type comes from the state rather than the URL because the
  // list is all one type by construction — quotes from the quotes tab,
  // invoices from the invoices tab, never mixed.
  const stepTo = (nextPos) => {
    if (nextPos < 0 || nextPos >= navIds.length) return;
    const nextId = navIds[nextPos];
    navigate(`/view-document/${docNav.type || type}/${nextId}`, {
      replace: true,
      state: { docNav: { ...docNav, index: nextPos } },
    });
  };
  const { isAdmin, isSalesperson, userName: roleName, loading: roleLoading } = useRole();
  const userName = roleName || user?.email?.split('@')[0]?.toUpperCase();
  const { products, updateProduct, customers } = useData();

  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPackingSlip, setShowPackingSlip] = useState(false);
  const [printCopies, setPrintCopies] = useState(3);
  const [printMode, setPrintMode] = useState(null); // null | 'multi'
  const [inventoryBusy, setInventoryBusy] = useState(false); // guards Mark/Revert inventory actions
  const [pickBusy, setPickBusy] = useState(false);           // guards Send/Cancel pick actions
  const [converting, setConverting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [docToDelete, setDocToDelete] = useState(null);

  // Payment state
  const [paymentHistory, setPaymentHistory] = useState([]);
  // 'loading' | 'ok' | 'error'. An empty paymentHistory means "nothing paid"
  // only when this is 'ok' — a failed read must never pass for an unpaid invoice.
  const [paymentsLoad, setPaymentsLoad] = useState('loading');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    cashReceived: '',
    method: 'Cash',
    checkNumber: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState('');
  // True while the Tag Made tick is being written.
  const [tagBusy, setTagBusy] = useState(false);


  // Shop drawings linked to THIS document. Read-only here: the link is set from
  // the drawing side, which is the side that knows which document it is for.
  const [linkedDrawings, setLinkedDrawings] = useState([]);

  useEffect(() => {
    loadDocument();
  }, [id, type]);

  // ── Linked shop drawings ──
  //
  // ONE EQUALITY FILTER, NO orderBy — so Firestore's automatic single-field
  // index covers it and there is nothing to deploy. The sort is done here
  // instead, exactly as CustomerProfile does when it fetches a customer's
  // drawings by customerId.
  useEffect(() => {
    let live = true;
    (async () => {
      if (!id) return;
      try {
        const snap = await getDocs(query(
          collection(db, 'shopDrawings'),
          where('linkedDocId', '==', id),
        ));
        if (!live) return;
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => String(a.drawingNumber || '').localeCompare(String(b.drawingNumber || '')));
        setLinkedDrawings(rows);
      } catch (err) {
        // The document itself must still render if this fails.
        console.error('Error loading linked drawings:', err);
        if (live) setLinkedDrawings([]);
      }
    })();
    return () => { live = false; };
  }, [id]);

  const loadDocument = async () => {
    setLoading(true);
    try {
      const col = type === 'quote' ? 'quotes' : 'invoices';
      const snap = await getDoc(doc(db, col, id));
      if (snap.exists()) {
        // Spread snap.id last so it always wins over any stale 'id' field in the stored data
        setDocument({ ...snap.data(), id: snap.id });
      } else {
        // CLEARED, not left alone. This only ever ran once per mount before, so
        // a miss simply showed "Document not found." Now that next/previous can
        // load a second document into the same mounted page, leaving the old
        // one in state would render the PREVIOUS document's number, lines and
        // totals under the new document's URL — silently, and convincingly.
        setDocument(null);
      }
      if (type === 'invoice') {
        await loadPaymentHistory();
      }
    } catch (err) {
      console.error('Error loading document:', err);
    }
    setLoading(false);
  };

  const loadPaymentHistory = async () => {
    setPaymentsLoad('loading');
    try {
      setPaymentHistory(await fetchInvoicePayments(id));
      setPaymentsLoad('ok');
    } catch (err) {
      console.error('Error loading payment history:', err);
      // Cleared, so a previous invoice's payments (next/previous stepping) can
      // never be shown under this one. The 'error' state is what the screen
      // reads — it shows "payments not loaded", not "unpaid".
      setPaymentHistory([]);
      setPaymentsLoad('error');
    }
  };

  // Customer phone for the Bill To line. Prefer the value stored on the document
  // (NewDocument saves customerPhone); fall back to a live lookup by customerId so
  // it also shows for older/edited docs that never stored the field. Empty string
  // when neither has it, so the render can skip the separator entirely.
  const customerPhone = document?.customerPhone || customers.find(c => c.id === document?.customerId)?.phone || '';
  // The customer's live record, for the credit-hold warning only. The document
  // carries a SNAPSHOT of the customer's details; a hold is a status, and is
  // deliberately not snapshotted onto documents.
  const liveCustomer = customers.find(c => c.id === document?.customerId) || null;

  // ── PAYMENT STATUS — COMPUTED, NEVER READ FROM THE INVOICE'S STORED FLAGS ──
  //
  // The grand total is the document AS SAVED (what was printed, what payments
  // were taken against); the payments are the records in the collection. The
  // header badge, the totals block, the printed Balance Due, the Record Payment
  // button and the payment modal ALL read this one result. They used to read
  // two different things — the badge and the button read the stored isPaid,
  // the totals read live values — which is how one screen could say PAID IN
  // FULL and Balance Due $200 at the same time. See utils/paymentStatus.js.
  const grandTotal = round2(document?.grandTotal || 0);
  const payStatus = getPaymentStatus(grandTotal, paymentHistory);
  const { totalPaid, balanceDue, creditAmount } = payStatus;
  const paymentsKnown = paymentsLoad === 'ok';

  // Cash "change due" math, off the cent-rounded balanceDue, so float residue
  // cannot render an exact payment as "Change: $0.00".
  const isCash = paymentForm.method === 'Cash';
  const cashReceived = parseFloat(paymentForm.cashReceived) || 0;
  // Cash is capped at the balance because anything handed over beyond it goes
  // straight back across the counter as change — it was never paid. Card,
  // check and other payments are recorded EXACTLY as entered: an overpayment
  // there is real money we are holding, and it shows as a credit.
  const recordedAmount = isCash ? round2(Math.min(cashReceived, balanceDue)) : round2(parseFloat(paymentForm.amount) || 0);
  // Display only — never written to the payments collection.
  const changeDue = isCash ? Math.max(0, round2(cashReceived - balanceDue)) : 0;

  const canEdit = !roleLoading && (isAdmin || document?.salesperson === userName);

  const initiateDelete = (docObj) => {
    if (!isAdmin) { alert('Only admins can delete documents.'); return; }
    setDocToDelete(docObj);
    setDeletePassword('');
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!docToDelete) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, deletePassword);
      await reauthenticateWithCredential(auth.currentUser, credential);

      const collectionName = docToDelete.docType === 'quote' ? 'quotes' : 'invoices';
      await deleteDoc(doc(db, collectionName, docToDelete.id));

      setShowDeleteModal(false);
      setDocToDelete(null);
      setDeletePassword('');
      navigate('/documents');
    } catch (err) {
      console.error('Delete error:', err);
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setDeleteError('❌ Incorrect password. Please try again.');
      } else {
        setDeleteError('❌ Error: ' + err.message);
      }
      setDeleting(false);
    }
  };

  const handleClone = async (targetType) => {
    setCloning(true);
    try {
      const newDocNumber = await generateDocNumber(targetType);
      const now = new Date().toISOString();

      // A clone is a NEW order for the same customer and materials. Nothing
      // about what happened to the old one carries over.
      //
      // So this is a list of what to KEEP, not of what to strip. It used to be
      // the other way round — copy everything, remove a few payment fields — and
      // every per-order field added since (board comments, pick request, picked
      // lines, Tag Made, pickup signature, inventory deduction, …) leaked into
      // clones. A field added to documents in future is left behind here unless
      // someone decides it belongs to a new order.
      //
      // P.O. is NOT kept: a P.O. number identifies one purchase from the
      // customer's side, and two orders under the same P.O. muddle their
      // billing. Notes ARE kept: they are instructions about the job and the
      // materials, printed on the document, and are editable on the clone.
      const KEEP = [
        // Customer (snapshots taken when the original was saved)
        'customerId', 'customerName', 'customerAddress', 'customerNumber', 'customerPhone',
        'customerAccount', 'taxExempt',
        // Materials and pricing
        'priceLevel', 'lineItems', 'subtotal', 'deliveryFee', 'tax', 'grandTotal',
        'discountPercent', 'discountAmount',
        'estimates', 'estimatorSpecs', 'fromEstimator',
        // Delivery
        'isDelivery', 'deliveryStreet', 'deliveryCity', 'deliveryState', 'deliveryZip',
        // Notes
        'notes',
      ];
      const kept = {};
      KEEP.forEach(f => { if (document[f] !== undefined) kept[f] = document[f]; });

      const cloneData = {
        ...kept,
        docNumber: newDocNumber,
        docType: targetType,
        // Whoever makes the clone is selling the new order — and can therefore
        // edit it, which the editor it opens in next requires.
        salesperson: userName || document.salesperson || '',
        poNumber: '',
        createdAt: now,
        date: now,
        clonedFrom: document.docNumber,
        ...(targetType === 'invoice' && {
          // The clone's id is brand new, so the payments collection holds
          // nothing for it: the mirror is derived from exactly that — no records.
          ...paymentMirrorFields(document.grandTotal, []),
          payments: [],
          status: DEFAULT_BOARD_STATUS,
          statusUpdatedAt: now,
          // Manual inventory model, as for a converted quote: nothing deducted.
          inventoryStatus: 'pending',
        }),
      };

      const col = targetType === 'quote' ? 'quotes' : 'invoices';
      const newRef = await addDoc(collection(db, col), cloneData);
      setShowCloneModal(false);
      navigate(`/edit-document/${targetType}/${newRef.id}`);
    } catch (err) {
      console.error('Clone error:', err);
      alert('Error cloning. Please try again.');
    }
    setCloning(false);
  };

  const updateBoardStatus = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await updateDoc(doc(db, 'invoices', id), {
        status: newStatus,
        statusUpdatedAt: new Date().toISOString(),
      });
      setDocument(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error('Error updating board status:', err);
      alert('Error updating status. Please try again.');
    }
    setUpdatingStatus(false);
  };

  // ── Manual inventory: Mark as Deducted / Revert to Pending (invoices only) ──
  // A pending invoice has an empty ledger, so the full quantity is the delta to
  // deduct. Reverting restores exactly what was deducted (negated ledger) and
  // clears the ledger. applyInventoryDelta uses increment(-delta): delta>0 lowers
  // stock (deduct), delta<0 raises it (restore); the cache patch mirrors it.
  const markAsDeducted = async () => {
    const currentQuantities = getDeductableQuantities(document.lineItems, products);
    const changes = computeInventoryDelta(currentQuantities, {}); // pending = empty baseline = full deduction
    if (changes.length === 0) {
      alert('No stock-tracked items to deduct on this invoice.');
      return;
    }
    const rows = describeDelta(changes, products);
    const lines = rows.map(r => `  −${r.qty}  ${r.sku} — ${r.description}`).join('\n');
    if (!window.confirm(`Deduct these from inventory?\n\n${lines}\n\nThis marks the invoice as DEDUCTED.`)) return;
    setInventoryBusy(true);
    try {
      await applyInventoryDelta({ changes, products, docId: id, docType: 'invoice', docNumber: document.docNumber, user, db });
      changes.forEach(c => {
        const p = products.find(pp => pp.id === c.productId);
        if (p) updateProduct(c.productId, { stockQty: (parseFloat(p.stockQty) || 0) - c.delta });
      });
      await updateDoc(doc(db, 'invoices', id), {
        deductedInventory: buildUpdatedLedger(currentQuantities),
        inventoryStatus: 'deducted',
        inventoryDeductedAt: new Date().toISOString(),
      });
      setDocument(prev => ({ ...prev, deductedInventory: buildUpdatedLedger(currentQuantities), inventoryStatus: 'deducted' }));
    } catch (e) {
      console.error('Mark as deducted failed:', e);
      alert(`⚠️ Could not deduct inventory.\n\n${e?.message || e}`);
    } finally {
      setInventoryBusy(false);
    }
  };

  const revertToPending = async () => {
    const ledger = getEffectiveLedger(document, document.lineItems, products); // what was actually deducted
    // Restore = delta from the current ledger back to empty → all-negative deltas.
    const changes = computeInventoryDelta({}, ledger); // target empty, baseline = ledger → all restores
    if (changes.length === 0) {
      // nothing was deducted; just flip status
      await updateDoc(doc(db, 'invoices', id), { inventoryStatus: 'pending', deductedInventory: {} });
      setDocument(prev => ({ ...prev, inventoryStatus: 'pending', deductedInventory: {} }));
      return;
    }
    const rows = describeDelta(changes, products);
    const lines = rows.map(r => `  +${r.qty}  ${r.sku} — ${r.description}`).join('\n');
    if (!window.confirm(`Return these to inventory and mark the invoice PENDING?\n\n${lines}`)) return;
    setInventoryBusy(true);
    try {
      await applyInventoryDelta({ changes, products, docId: id, docType: 'invoice', docNumber: document.docNumber, user, db });
      changes.forEach(c => {
        const p = products.find(pp => pp.id === c.productId);
        if (p) updateProduct(c.productId, { stockQty: (parseFloat(p.stockQty) || 0) - c.delta }); // c.delta is negative here → adds back
      });
      await updateDoc(doc(db, 'invoices', id), { inventoryStatus: 'pending', deductedInventory: {}, inventoryDeductedAt: null });
      setDocument(prev => ({ ...prev, inventoryStatus: 'pending', deductedInventory: {} }));
    } catch (e) {
      console.error('Revert to pending failed:', e);
      alert(`⚠️ Could not revert inventory.\n\n${e?.message || e}`);
    } finally {
      setInventoryBusy(false);
    }
  };

  // ── Send to shop / cancel (invoices only) ──
  //
  // Deliberately NOT wired to inventory. Sending an invoice to be picked says
  // "go and gather this"; deducting says "the stock figures now reflect it".
  // The warehouse does the first and the office does the second, often days
  // apart, and making one imply the other would put the stock count wrong.
  // These handlers touch pick* fields and nothing else.
  const sendToShop = async () => {
    if (!window.confirm(`Send invoice #${document.docNumber} to the shop to be picked?`)) return;
    setPickBusy(true);
    try {
      const fields = pickRequestFields(userName);
      await updateDoc(doc(db, 'invoices', id), fields);
      setDocument(prev => ({ ...prev, ...fields }));
    } catch (e) {
      console.error('Send to shop failed:', e);
      alert(`⚠️ Could not send to the shop.\n\n${e?.message || e}`);
    } finally {
      setPickBusy(false);
    }
  };

  const cancelPickRequest = async () => {
    if (!window.confirm(`Cancel the picking request for #${document.docNumber}? It will disappear from the shop's list.`)) return;
    setPickBusy(true);
    try {
      const fields = pickCancelFields();
      await updateDoc(doc(db, 'invoices', id), fields);
      setDocument(prev => ({ ...prev, ...fields }));
    } catch (e) {
      console.error('Cancel pick request failed:', e);
      alert(`⚠️ Could not cancel the request.\n\n${e?.message || e}`);
    } finally {
      setPickBusy(false);
    }
  };

  // ── Tag Made (invoices only, admin only) ──
  //
  // The office writes "TM" on an invoice once a gate tag (a shop drawing) or
  // plated posts have been made for the order, so the warehouse knows the tag
  // exists. This is that note, as one flag on the invoice — not per line.
  //
  // Ticking records who and when. Unticking clears all three fields: a flag
  // that is off but still names somebody reads as "they made it, then someone
  // took it back", which is not what unticking means.
  //
  // Touches these three fields and nothing else — no inventory, no pick state.
  const toggleTagMade = async (checked) => {
    setTagBusy(true);
    try {
      const fields = checked
        ? { tagMade: true, tagMadeBy: userName || '', tagMadeAt: new Date().toISOString() }
        : { tagMade: false, tagMadeBy: null, tagMadeAt: null };
      await updateDoc(doc(db, 'invoices', id), fields);
      setDocument(prev => ({ ...prev, ...fields }));
    } catch (e) {
      console.error('Tag Made update failed:', e);
      alert(`⚠️ Could not save Tag Made.\n\n${e?.message || e}`);
    } finally {
      setTagBusy(false);
    }
  };

  // Tick a line the customer collected at the counter. Same write as the shop
  // tablet, through the same helper — and, like it, NOTHING to do with
  // inventory: the stock left the count when this invoice was deducted.
  const toggleCollected = async (item) => {
    const fields = toggleItemFields(document, item?.id);
    if (!fields) return;
    try {
      await updateDoc(doc(db, 'invoices', id), fields);
      // Local echo. The write above uses arrayUnion/arrayRemove so two people
      // ticking at once cannot clobber; this only keeps THIS screen current.
      setDocument(prev => ({ ...prev, pickedItemIds: toggledItemIds(prev, item.id) }));
    } catch (e) {
      console.error('Could not update collected items:', e);
      alert(`⚠️ Could not save.\n\n${e?.message || e}`);
    }
  };

  // Back to wherever they came from, on the tab they left. Falls through to
  // ordinary history when there is no list — an unchanged path for every other
  // way of reaching this page.
  const backToList = () => {
    if (docNav?.returnTo) navigate(docNav.returnTo, { state: { returnTab: docNav.returnTab } });
    else navigate(-1);
  };

  const handlePrint = () => {
    setShowPackingSlip(false);
    window.print();
  };

  // Print 2 full priced copies + 1 packing slip (3 sheets) immediately — no
  // prompt. The multi-copy render block reads printCopies and emits the LAST
  // sheet as the packing slip, so 3 copies = 2 priced + 1 slip.
  const handleMultiPrint = () => {
    setPrintCopies(3);
    setPrintMode('multi');
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintMode(null), 500);
    }, 300);
  };

  const handlePrintPackingSlip = () => {
    setShowPackingSlip(true);
    setTimeout(() => window.print(), 300);
  };

  // Conversion (create invoice + inventory deduction + quote-marking) lives in the
  // shared util so the edit screen converts identically. This handler owns only the
  // preview's confirm dialog, success alert, and navigation.
  const handleConvertToInvoice = async () => {
    // Block double-conversion: a quote can only be converted once. If it already
    // carries the marker, offer to open the existing invoice instead.
    const alreadyNumber = document?.convertedToInvoice;
    if (alreadyNumber) {
      const existingId = document?.convertedToInvoiceId || null;
      const go = window.confirm(
        `⛔ This quote was already converted to invoice #${alreadyNumber}.\n\nA quote can only be converted once.\n\nOK = open invoice #${alreadyNumber}\nCancel = stay here`
      );
      if (go) {
        if (existingId) {
          navigate(`/view-document/invoice/${existingId}`);
        } else {
          // Legacy quote: no stored ID. Look up the invoice by number.
          try {
            const q = query(collection(db, 'invoices'), where('docNumber', '==', alreadyNumber), limit(1));
            const snap = await getDocs(q);
            if (!snap.empty) {
              navigate(`/view-document/invoice/${snap.docs[0].id}`);
            } else {
              alert(`Invoice #${alreadyNumber} could not be found. It may have been deleted.`);
            }
          } catch (e) {
            console.error('Lookup of converted invoice failed:', e);
            alert(`Could not open invoice #${alreadyNumber}.`);
          }
        }
      }
      return; // hard stop — never convert again
    }
    if (!window.confirm(`Convert Quote ${document.docNumber} to ${wordTitle}? This cannot be undone.`)) return;
    setConverting(true);
    try {
      const { invoiceId, invoiceNumber } = await convertQuoteToInvoice({
        quoteId: id, quoteData: document, isAccount, products, updateProduct, user,
      });
      alert(`Quote converted to ${wordTitle} ${invoiceNumber} successfully!`);
      navigate(`/edit-document/invoice/${invoiceId}`);
    } catch (err) {
      console.error('Conversion error:', err);
      alert('Error converting. Please try again.');
    }
    setConverting(false);
  };

  const openPaymentModal = () => {
    setPaymentForm({
      amount: balanceDue > 0 ? balanceDue.toFixed(2) : '',
      cashReceived: '',
      method: 'Cash',
      checkNumber: '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
    });
    setShowPaymentModal(true);
  };

  const handleRecordPayment = async () => {
    // Cash: min(cashReceived, balanceDue) — the excess is change handed back.
    // Anything else: exactly as entered, overpayment included (it shows as a credit).
    const amount = recordedAmount;
    if (amount <= 0) {
      alert(isCash ? 'Please enter the cash amount received.' : 'Please enter a valid payment amount.');
      return;
    }
    setRecordingPayment(true);
    let recorded = false;
    try {
      // A label on the payment RECORD — "this one settled the invoice, when it
      // was taken". Nothing reads it, and it is not the invoice's status, which
      // is rebuilt from the collection below.
      const isFullPayment = isSettled(getPaymentStatus(grandTotal, [...paymentHistory, { amount }]).status);

      await addDoc(collection(db, 'payments'), {
        invoiceId: id,
        invoiceNumber: document.docNumber,
        customerId: document.customerId,
        customerName: document.customerName,
        amount,
        method: paymentForm.method,
        // Informational only — `amount` above (the capped balance) is what counts
        // toward totalPaid. These record what physically changed hands at the till.
        cashReceived: isCash ? cashReceived : null,
        changeDue: isCash ? changeDue : null,
        checkNumber: paymentForm.method === 'Check' ? paymentForm.checkNumber : '',
        date: paymentForm.date,
        notes: paymentForm.notes,
        recordedBy: userName,
        createdAt: new Date().toISOString(),
        isFullPayment,
      });
      recorded = true;

      // Rebuild the invoice's stored mirror from the payments COLLECTION — which
      // now includes the record just written — and the invoice's CURRENT grand
      // total. It used to write "stored paidAmount + this amount", a second
      // copy of the number that drifted on its own.
      await syncInvoicePaymentMirror(id, {
        paidAt: paymentForm.date,
        extra: { paymentMethod: paymentForm.method },
      });

      setShowPaymentModal(false);
      setPaymentSuccess(`✅ Payment of $${amount.toFixed(2)} recorded successfully!`);
      setTimeout(() => setPaymentSuccess(''), 5000);
    } catch (err) {
      console.error('Error recording payment:', err);
      if (recorded) {
        // The money IS recorded. "Try again" here would invite a second, identical payment.
        setShowPaymentModal(false);
        alert(mirrorSyncFailedMessage(`The payment of $${amount.toFixed(2)} WAS recorded. Do not enter it again.`));
      } else {
        alert('Error recording payment. Nothing was recorded. Please try again.');
      }
    }
    if (recorded) await loadDocument();
    setRecordingPayment(false);
  };

  // Delete a single saved payment (admin only). Touches ONLY the payments
  // collection + the invoice's paid-status fields — never inventory/line items.
  const handleDeletePayment = async (pmt) => {
    const amount = parseFloat(pmt.amount || 0);
    if (!window.confirm(`Delete this payment of $${amount.toFixed(2)}? This cannot be undone.`)) return;
    let deleted = false;
    try {
      // 1. Remove the individual payment record from the collection.
      await deleteDoc(doc(db, 'payments', pmt.id));
      deleted = true;

      // 2. Reconcile the embedded `payments` array so the deleted payment can't
      //    be "resurrected" if the invoice is later opened/saved in EditDocument.
      //    Embedded entries carry no id, so match by value on the available
      //    fields and remove only the FIRST match. Fetch fresh to avoid acting
      //    on a stale in-memory copy; missing match is fine (proceed gracefully).
      let reconciledPayments;
      try {
        const invSnap = await getDoc(doc(db, 'invoices', id));
        const currentArray = (invSnap.exists() && invSnap.data().payments) || [];
        const idx = currentArray.findIndex(p =>
          parseFloat(p.amount || 0) === amount &&
          (p.date || '') === (pmt.date || '') &&
          (p.method || '') === (pmt.method || '') &&
          (p.checkNumber || '') === (pmt.checkNumber || '') &&
          (p.notes || '') === (pmt.notes || '')
        );
        if (idx === -1) {
          reconciledPayments = currentArray;
        } else {
          reconciledPayments = [...currentArray];
          reconciledPayments.splice(idx, 1);
        }
      } catch (e) {
        // If we can't read the array, leave it untouched rather than guessing.
        reconciledPayments = undefined;
      }

      // 3. Rebuild the stored mirror from the payments STILL IN THE COLLECTION,
      //    in one write with the reconciled array. Not "the list on screen minus
      //    this one": that list is from when the page opened, and another
      //    payment may have been recorded since.
      await syncInvoicePaymentMirror(id, {
        extra: reconciledPayments !== undefined ? { payments: reconciledPayments } : {},
      });
    } catch (err) {
      console.error('Error deleting payment:', err);
      alert(deleted
        ? mirrorSyncFailedMessage(`The payment of $${amount.toFixed(2)} WAS deleted.`)
        : 'Error deleting payment. Nothing was deleted. Please try again.');
    }
    // 4. Refresh the document and its payment records.
    if (deleted) await loadDocument();
  };

  // Account vs regular → the invoice-type label word. Prefer the document's
  // snapshot; else look the customer up by customerId; default REGULAR only when
  // truly unresolved (NOT merely because the snapshot is absent). Memoized on
  // [document, customers] so it re-resolves once customers finish loading async —
  // and so EVERY label below reads the exact same value.
  const isAccount = useMemo(() => (
    document?.customerAccount != null
      ? document.customerAccount
      : (customers.find(c => c.id === document?.customerId)?.isAccountCustomer ?? false)
  ), [document, customers]);
  const wordCaps = isAccount ? 'INVOICE' : 'SALE';
  const wordTitle = isAccount ? 'Invoice' : 'Sale';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#CDE7F5', fontFamily: 'Manrope, sans-serif' }}>
        <p className="text-xl" style={{ color: '#6B7C93' }}>Loading document...</p>
      </div>
    );
  }

  if (!document) {
    // ── A DOCUMENT THAT IS NO LONGER THERE ──
    //
    // The id list is a snapshot from when the user left the customer profile.
    // If somebody deleted one of those documents in the meantime, stepping onto
    // it lands here. It does not crash — loadDocument simply finds nothing and
    // leaves `document` null — but the plain "not found" that used to render
    // was a dead end with no way out except the browser's Back button.
    //
    // So while stepping through a list it says WHY, and keeps the controls: the
    // user can carry on to the next one or go back to the customer. The gap
    // stays in the numbering ("4 of 12" with nothing at 4) rather than the list
    // silently renumbering itself, because a position that shifts underneath
    // somebody is worse than a position that is briefly empty.
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#CDE7F5', fontFamily: 'Manrope, sans-serif' }}>
        {showDocNav && (
          <div style={{ maxWidth: 896, margin: '0 auto', padding: '24px 16px 0 16px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 12,
              padding: '10px 14px',
            }}>
              <button
                onClick={backToList}
                style={{
                  background: 'transparent', border: '1px solid #E3ECF5', borderRadius: 8,
                  padding: '6px 12px', fontSize: 13, fontWeight: 700, color: '#5A6B80',
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                ← Back to customer
              </button>
              <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700, color: '#0B2545' }}>
                {navPos + 1} of {navIds.length}
                <span style={{ fontWeight: 500, color: '#5A6B80' }}>
                  {' '}{docNav.type === 'quote' ? 'quotes' : 'invoices'}
                </span>
              </span>
              <button onClick={() => stepTo(navPos - 1)} disabled={!hasPrev} aria-label="Previous document" style={navStepStyle(!hasPrev)}>
                ← Previous
              </button>
              <button onClick={() => stepTo(navPos + 1)} disabled={!hasNext} aria-label="Next document" style={navStepStyle(!hasNext)}>
                Next →
              </button>
            </div>
          </div>
        )}
        <div className="flex items-center justify-center" style={{ minHeight: showDocNav ? '60vh' : '100vh' }}>
          <p className="text-xl" style={{ color: '#A32D2D' }}>
            {showDocNav
              ? 'This document has been deleted since you opened the list.'
              : 'Document not found.'}
          </p>
        </div>
      </div>
    );
  }

  const subtotal = round2(document.subtotal || 0);
  const deliveryFee = round2(document.deliveryFee || 0);
  const discountPercent = parseFloat(document.discountPercent || 0); // a percentage, not money
  const discountAmount = round2(document.discountAmount || 0);
  const tax = round2(document.tax || 0);
  // Older invoices have no tagMade field at all; anything but a real `true`
  // counts as not made, so a stray value ("false", 0, "yes") can never print
  // the slip's Tag made box as marked.
  const tagMade = document.tagMade === true;

  // Header badge — reads payStatus, the same result the totals block reads.
  const paymentStatusBadge = () => {
    if (!paymentsKnown) return (
      <span className="font-bold px-3 py-1 rounded-lg text-sm" style={{ backgroundColor: '#FDF1DD', color: '#854F0B' }}
        title="The payment records for this invoice could not be read, so whether it is paid is not known. Reload the page.">
        ⚠️ PAYMENTS NOT LOADED
      </span>
    );
    if (payStatus.status === PAYMENT_STATUS.CREDIT) return (
      <span className="font-bold px-3 py-1 rounded-lg text-sm" style={{ backgroundColor: '#E6F1FB', color: '#0077B6' }}>
        💙 CREDIT — ${creditAmount.toFixed(2)} overpaid
      </span>
    );
    if (payStatus.status === PAYMENT_STATUS.PAID) return (
      <span className="font-bold px-3 py-1 rounded-lg text-sm flex items-center gap-1" style={{ backgroundColor: '#E6F7EE', color: '#0F6E56' }}>
        {METHOD_ICON[paymentHistory[paymentHistory.length - 1]?.method] || '✅'} PAID IN FULL
      </span>
    );
    if (payStatus.status === PAYMENT_STATUS.PARTIAL) return (
      <span className="font-bold px-3 py-1 rounded-lg text-sm" style={{ backgroundColor: '#FDF1DD', color: '#854F0B' }}>
        💛 PARTIAL — ${balanceDue.toFixed(2)} remaining
      </span>
    );
    return (
      <span className="font-bold px-3 py-1 rounded-lg text-sm" style={{ backgroundColor: '#FBECEC', color: '#A32D2D' }}>
        🔴 UNPAID
      </span>
    );
  };

  // ── Packing Slip ──
  // Packing slip printable body (no outer page wrapper, no action buttons).
  // Used by both the single packing-slip view and the multi-copy print stack.
  const renderPackingSlipBody = () => (
    <>
      <div className="flex items-start justify-between mb-4 pb-3 border-b-2 border-gray-800">
        <div className="flex items-center gap-3">
          <img
            src={require('../assets/atlantic-logo-2D.png')}
            alt="Atlantic Fence & Supply"
            className="w-14 h-14 object-contain flex-shrink-0 print:w-16 print:h-16"
          />
          <div>
            <h1 className="text-lg font-bold text-blue-900">Atlantic Fence & Supply</h1>
            <p className="text-gray-500 text-xs">1803 Dorsey Rd, Hanover MD 21076</p>
            <p className="text-gray-500 text-xs">Phone: 410-796-4747 | Fax: 410-796-0032</p>
          </div>
        </div>
        <div className="text-right">
          <div className="bg-gray-800 text-white px-4 py-1 rounded mb-1">
            <p className="text-sm font-bold">PACKING SLIP</p>
          </div>
          {(document.isDelivery || parseFloat(document.deliveryFee || 0) > 0 || document.deliveryStreet) && (
            <span className="inline-block bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[10px] font-bold">🚚 DELIVERY ORDER</span>
          )}
          <p className="text-gray-700 font-mono font-bold">#{document.docNumber}</p>
          <p className="text-gray-500 text-xs">Date: {new Date(document.createdAt).toLocaleDateString('en-US')}</p>
          <p className="text-gray-500 text-xs">Salesperson: <span className="font-bold">{document.salesperson}</span></p>
        </div>
      </div>

      <div className="bg-gray-50 rounded p-2 mb-3">
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-bold text-gray-800 min-w-0">{document.customerName}</p>
          {document.poNumber && (
            <p className="text-gray-600 text-xs flex-shrink-0">
              <span className="uppercase font-bold text-gray-400 text-[10px] tracking-wide">P.O. #: </span>
              <span className="font-bold text-gray-700">{document.poNumber}</span>
            </p>
          )}
        </div>
        {document.isDelivery && document.deliveryStreet && (
          <p className="text-gray-600 text-xs mt-0.5">
            <span className="uppercase font-bold text-gray-400 text-[10px] tracking-wide">Ship To: </span>
            {[document.deliveryStreet, document.deliveryCity, document.deliveryState, document.deliveryZip].filter(Boolean).join(', ')}
          </p>
        )}
      </div>

      <table className="w-full mb-4 text-sm">
        <thead>
          <tr className="bg-gray-800 text-white">
            <th className="px-2 py-1 text-center text-xs" style={{ width: 36 }}>✓</th>
            <th className="px-2 py-1 text-center text-xs">Qty</th>
            <th className="px-2 py-1 text-left text-xs">UM</th>
            <th className="px-2 py-1 text-left text-xs">SKU</th>
            <th className="px-2 py-1 text-left text-xs">Description</th>
            <th className="px-2 py-1 text-center text-xs">Stock</th>
          </tr>
        </thead>
        <tbody>
          {document.lineItems?.map((item, index) => (
            item.isTextNote ? (
              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td colSpan={6} className="px-2 py-0.5 text-xs">{item.description}</td>
              </tr>
            ) : (
            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              {/* Empty pick/pack checkbox — styled div (not an <input>) so it prints
                  as a clean empty square across browsers; filled in by hand */}
              <td className="px-2 py-0.5 text-center" style={{ verticalAlign: 'middle' }}>
                <div style={{
                  width: 14, height: 14,
                  border: '2px solid #333', borderRadius: 3,
                  background: 'white', display: 'inline-block',
                }} />
              </td>
              <td className="px-2 py-0.5 text-center text-xs font-bold">{item.qty}</td>
              <td className="px-2 py-0.5 text-gray-500 text-xs">{item.unit}</td>
              <td className="slip-sku px-2 py-0.5 text-blue-700 font-mono text-xs font-bold">{item.sku}</td>
              <td className="px-2 py-0.5 text-xs">
                {item.description}
                {item.isReturnCredit && (
                  <span className="ml-1 font-bold" style={{ fontSize: 11, color: '#A32D2D' }}>↩ RETURN</span>
                )}
                {item.isNonStockCustom && (
                  <span className="ns-inline ns-inline-slip ml-1 font-bold italic" style={{ fontSize: 11, color: '#854F0B' }}>NON-STOCK</span>
                )}
              </td>
              {/* The Stock column. See PRINT_BADGE_CSS: on paper these pills
                  shrink, because their width comes out of the Description
                  column on every row of the slip. */}
              <td className="px-2 py-0.5 text-center">
                {item.isNonStockCustom ? (
                  <span className="stock-pill stock-pill-ns bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-bold">NON-STOCK</span>
                ) : item.isStock ? (
                  <span className="stock-pill bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-bold">STOCK</span>
                ) : (
                  <span className="stock-pill stock-pill-ns bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-bold">NON-STOCK</span>
                )}
              </td>
            </tr>
            )
          ))}
        </tbody>
      </table>

      {/* ── PACKING CONFIRMATIONS — one line, two pen boxes ──
          Both are things the dispatcher confirms while packing, so Tag Made
          sits beside "All items verified and packed" rather than on a line of
          its own. Two items on the row that already existed: same 18px boxes,
          same 12px bold labels, so the row is exactly as tall as before.

          TAG MADE PRINTS ON EVERY SLIP. When the office has not ticked it in
          the system the box is empty, and the shop can mark it by hand — which
          is the point of printing it regardless.

          When the flag IS set (a real `true` only — see tagMade), the box
          prints already marked with an X: two strokes of an inline SVG inside
          the square. Vector strokes print on every printer. A filled square
          would not — browsers drop backgrounds by default — and a ✓ character
          depends on the print font having the glyph. An X in a box is also
          what a paper form's "checked" has always looked like, so nobody
          ticks it again. */}
      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 32, pageBreakInside: 'avoid' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 18, height: 18,
            border: '2px solid #333', borderRadius: 3,
            background: 'white', flexShrink: 0,
          }} />
          <span style={{ fontSize: 12, fontWeight: 'bold' }}>All items verified and packed</span>
        </div>
        <div data-tag-made={tagMade ? 'marked' : 'blank'} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 18, height: 18,
            border: '2px solid #333', borderRadius: 3,
            background: 'white', flexShrink: 0,
          }}>
            {tagMade && (
              <svg viewBox="0 0 14 14" width="14" height="14" style={{ display: 'block' }} aria-label="Tag made — marked in the system">
                <path d="M2.5 2.5 L11.5 11.5 M11.5 2.5 L2.5 11.5" stroke="#000" strokeWidth="2.2" strokeLinecap="round" fill="none" />
              </svg>
            )}
          </div>
          <span style={{ fontSize: 12, fontWeight: 'bold' }}>Tag made (TM)</span>
        </div>
      </div>

      <div style={{ marginTop: 12, pageBreakInside: 'avoid' }}>
        <p style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Dispatcher Notes:
        </p>
        <div style={{ border: '1px solid #333', borderRadius: 4, minHeight: 48, width: '100%' }} />
      </div>

      {/* Two columns, one line each — the dispatcher's and the customer's,
          exactly as this sheet was laid out before signature capture existed.
          The 44px above them IS the space to sign in; when a signature was
          captured it is drawn in that space and the margin shrinks by the same
          amount, so the block's height does not change. */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        marginTop: hasSignature(document) ? 18 : 44,
        gap: 40, pageBreakInside: 'avoid',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ borderTop: '1px solid #333', paddingTop: 6 }}>
            <p style={{ fontSize: 11, fontWeight: 'bold', margin: 0 }}>Dispatcher Signature</p>
            <p style={{ fontSize: 10, color: '#666', margin: '2px 0 0 0' }}>Date: _______________</p>
          </div>
        </div>
        <div style={{ flex: 1 }}>
          {hasSignature(document) && (
            <SignatureView path={document.pickupSignature} height={26} style={{ marginBottom: 0 }} />
          )}
          <div style={{ borderTop: '1px solid #333', paddingTop: 6 }}>
            <p style={{ fontSize: 11, fontWeight: 'bold', margin: 0 }}>
              {hasSignature(document)
                ? document.pickupSignedName
                : 'Customer Signature (Received)'}
            </p>
            <p style={{ fontSize: 10, color: '#666', margin: '2px 0 0 0' }}>
              {hasSignature(document)
                ? `Date: ${fmtSignedAt(document.pickupSignedAt)}`
                  + (document.pickupWitnessedBy ? ` · witnessed by ${document.pickupWitnessedBy}` : '')
                : 'Date: _______________'}
            </p>
          </div>
        </div>
      </div>
      <p className="text-center text-gray-300 mt-6" style={{ fontSize: '7px' }}>powered by Fence Flow</p>
    </>
  );

  if (showPackingSlip) {
    return (
      <div className="min-h-screen bg-white px-8 py-6">
        <style>{PRINT_BADGE_CSS}</style>
        {renderPackingSlipBody()}
        <div className="mt-6 print:hidden flex gap-4">
          <button onClick={() => window.print()} className="bg-gray-800 text-white font-bold px-6 py-2 rounded-lg">
            🖨️ Print Packing Slip
          </button>
          <button onClick={() => setShowPackingSlip(false)} className="bg-gray-200 text-gray-700 font-bold px-6 py-2 rounded-lg">
            ← Back to Document
          </button>
        </div>
      </div>
    );
  }

  // Full invoice/quote printable body (no outer page wrapper).
  // Used by both the single-doc view and the multi-copy print stack.
  const renderFullDocBody = () => (
    <>
      {/* Header — three zones: company info left · logo center · order block right.
          grid-cols-[1fr_auto_1fr] keeps the logo on the page's horizontal midline
          regardless of the unequal left/right column widths. */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-3 mb-2 pb-1 border-b-2 border-blue-800">
        {/* LEFT: company info (left-aligned) — fax removed, "Phone" label removed */}
        <div>
          <h1 className="text-base font-bold text-blue-900 leading-tight">Atlantic Fence & Supply</h1>
          <p className="text-gray-500 text-[11px]">1803 Dorsey Rd, Hanover MD 21076</p>
          <p className="text-gray-500 text-[11px]">410-796-4747</p>
          <p className="text-gray-500 text-[11px]">www.atlanticfencesupply.com</p>
          {(document.isDelivery || parseFloat(document.deliveryFee || 0) > 0 || document.deliveryStreet) && (
            <span className="inline-block mt-1 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[10px] font-bold">🚚 DELIVERY ORDER</span>
          )}
        </div>
        {/* CENTER: enlarged 2D logo, centered in the 3-zone grid. require() bundles
            the asset so it prints reliably. */}
        <div className="flex justify-center">
          <img
            src={require('../assets/atlantic-logo-2D.png')}
            alt="Atlantic Fence & Supply"
            className="w-32 h-32 object-contain flex-shrink-0 print:w-28 print:h-28"
          />
        </div>
        {/* RIGHT: order block (unchanged this step) */}
        <div className="text-right">
          {/* flex justify-end pins the shrink-wrapped pill to the column's right edge
              in BOTH screen and print. Prefer this over w-fit + ml-auto: in print,
              Chromium resolves the 1fr column / fit-content width late, so ml-auto
              collapses to 0 and the pill drifts left. Flexbox free-space distribution
              is reliable across both media. */}
          <div className="flex justify-end mb-1">
            <div className="bg-blue-800 text-white px-4 py-1 rounded">
              <p className="text-sm font-bold">
                {/* Regular sale reads "SALE" on screen but "SALES ORDER" in print;
                    account "INVOICE" and quotes "SALE QUOTE" are identical in both. */}
                <span className="print:hidden">{type === 'quote' ? 'SALE QUOTE' : wordCaps}</span>
                <span className="hidden print:inline">
                  {type === 'quote' ? 'SALE QUOTE' : (isAccount ? 'INVOICE' : 'SALES ORDER')}
                </span>
              </p>
            </div>
          </div>
          <p className="text-blue-700 font-mono font-bold text-lg">#{document.docNumber}</p>
          {document.clonedFrom && (
            <p className="text-gray-400 text-xs mt-0.5 print:hidden">📋 Cloned from #{document.clonedFrom}</p>
          )}
          <p className="text-gray-500 text-xs">Date: {new Date(document.createdAt).toLocaleDateString('en-US')} · {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</p>
          <p className="text-gray-500 text-xs">Salesperson: <span className="font-bold text-gray-700">{document.salesperson}</span></p>
          {document.poNumber && (
            <p className="text-gray-500 text-xs">P.O. #: <span className="font-bold text-gray-700">{document.poNumber}</span></p>
          )}
          {/* Paid/unpaid status badge removed — the Balance Due line under Grand Total conveys it. */}
        </div>
      </div>

      {/* Customer Info — 3 columns: Customer (no heading) · Ship To · PO.
          Shared by screen + print (renderFullDocBody). Equal columns via
          grid-cols-3 (repeat(3, minmax(0,1fr))) — print-safe plain text grid;
          no fit-content/auto-margin, so no print drift. No wrapping box/border. */}
      <div className="grid grid-cols-3 gap-4 text-xs mb-2">
        {/* COLUMN 1 — Customer: no heading, just name · Customer # · phone */}
        <div className="min-w-0">
          <button onClick={() => navigate(`/customer-profile/${document.customerId}`)}
            className="font-bold text-sm hover:underline print:pointer-events-none text-left leading-snug"
            style={{ color: '#0077B6' }}>
            {document.customerName}
          </button>
          <p className="text-gray-900 leading-snug">Customer #: {document.customerNumber}</p>
          {customerPhone && <p className="text-gray-900 leading-snug">{customerPhone}</p>}
          {document.taxExempt && (
            <span className="inline-block mt-1 bg-green-100 text-green-700 px-2 py-0.5 rounded text-[10px] font-bold">TAX EXEMPT</span>
          )}
        </div>
        {/* COLUMN 2 — SHIP TO: heading always shown; address only when delivery, else empty */}
        <div className="min-w-0">
          <p className="uppercase font-bold text-gray-400 text-[10px] tracking-wide mb-0.5">Ship To</p>
          {document.isDelivery && document.deliveryStreet && (
            <p className="text-gray-500 leading-snug">
              {document.deliveryStreet}
              {[document.deliveryCity, document.deliveryState, document.deliveryZip].filter(Boolean).length > 0 && (
                <><br />{[document.deliveryCity, document.deliveryState, document.deliveryZip].filter(Boolean).join(', ')}</>
              )}
            </p>
          )}
        </div>
        {/* COLUMN 3 — PO: heading always shown; value only when present, else empty */}
        <div className="min-w-0">
          <p className="uppercase font-bold text-gray-400 text-[10px] tracking-wide mb-0.5">PO</p>
          {document.poNumber && <p className="text-gray-700 font-bold leading-snug">{document.poNumber}</p>}
        </div>
      </div>

      {/* Line Items Table */}
      <table className="w-full mb-4 text-sm">
        <thead>
          <tr className="bg-blue-800 text-white" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
            <th className="px-2 py-1 text-center text-xs">Qty</th>
            <th className="px-2 py-1 text-left text-xs">UM</th>
            <th className="px-2 py-1 text-left text-xs">Description</th>
            <th className="px-2 py-1 text-right text-xs">Unit Price</th>
            <th className="px-2 py-1 text-right text-xs">Total</th>
          </tr>
        </thead>
        <tbody>
          {document.lineItems?.map((item, index) => (
            item.isTextNote ? (
              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td colSpan={5} className="px-2 py-0.5 text-xs">{item.description}</td>
              </tr>
            ) : (
            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-2 py-0.5 text-center text-xs">{item.qty}</td>
              <td className="px-2 py-0.5 text-gray-500 text-xs">{item.unit}</td>
              <td className="px-2 py-0.5 text-xs">
                {item.description}
                {item.isReturnCredit && (
                  <span className="ml-1.5 font-semibold" style={{ fontSize: 10, color: '#A32D2D' }}>RETURN CREDIT</span>
                )}
                {item.isNonStockCustom && (
                  <span className="ns-inline ml-1.5 font-semibold italic" style={{ fontSize: 10, color: '#854F0B' }}>NON-STOCK</span>
                )}
              </td>
              <td className="px-2 py-0.5 text-right text-xs">{formatMoney4(parseFloat(item.unitPrice))}</td>
              <td className={`px-2 py-0.5 text-right font-bold text-xs ${item.isReturnCredit ? 'text-red-700' : ''}`}>
                {formatMoney(lineTotal(item))}
              </td>
            </tr>
            )
          ))}
          {document.lineItems?.length < 8 && Array.from({ length: Math.max(0, 2 - document.lineItems.length) }).map((_, i) => (
            <tr key={`empty-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-2 py-0.5 text-xs">&nbsp;</td>
              <td className="px-2 py-0.5 text-xs"></td>
              <td className="px-2 py-0.5 text-xs"></td>
              <td className="px-2 py-0.5 text-xs"></td>
              <td className="px-2 py-0.5 text-xs"></td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-end mb-2">
        {/* Compact totals — all rows sized text-xs to match the line-items table
            rows, tight spacing, no boxes. One thin rule above Grand Total; Grand
            Total bold (not bigger, not boxed). Values/order/conditional rows unchanged. */}
        <div className="w-56 text-xs">
          <div className="flex justify-between py-0.5">
            <span className="text-gray-600">Subtotal</span>
            <span className="font-bold">{formatMoney(subtotal)}</span>
          </div>
          {discountAmount > 0 && (
            <div className="flex justify-between py-0.5">
              <span className="text-gray-600">Discount ({discountPercent}%)</span>
              <span className="font-bold text-red-700">−{formatMoney(discountAmount)}</span>
            </div>
          )}
          <div className="flex justify-between py-0.5">
            <span className="text-gray-600">
              Tax (6% MD)
              {document.taxExempt && <span className="ml-1 text-green-600">EXEMPT</span>}
            </span>
            <span className="font-bold">{formatMoney(tax)}</span>
          </div>
          {deliveryFee > 0 && (
            <div className="flex justify-between py-0.5">
              <span className="text-gray-600">Delivery Fee</span>
              <span className="font-bold">${deliveryFee.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between py-0.5 border-t border-gray-300 mt-0.5">
            <span className="font-bold text-gray-800">GRAND TOTAL</span>
            <span className="font-bold text-blue-700">{formatMoney(grandTotal)}</span>
          </div>
          {/* Balance Due — invoices only (quotes have no payments). Always shown,
              even at $0.00. Reads payStatus, the SAME result as the header badge,
              so the two can never disagree.

              A credit gets its own line rather than a negative balance: "Balance
              Due −$150.00" reads as a typo, "Credit $150.00" reads as money held.

              If the payment records could not be read, the balance is unknown and
              prints as a dash rather than as the full total. */}
          {type === 'invoice' && (
            !paymentsKnown ? (
              <div className="flex justify-between py-0.5">
                <span className="font-bold text-gray-800">Balance Due</span>
                <span className="font-bold text-gray-500">—</span>
              </div>
            ) : (
              <>
                <div className="flex justify-between py-0.5">
                  <span className="font-bold text-gray-800">Balance Due</span>
                  {balanceDue > 0 ? (
                    <span className="font-bold text-red-600">{formatMoney(balanceDue)}</span>
                  ) : isSettled(payStatus.status) ? (
                    <span className="font-bold text-green-700">{formatMoney(0)} <span className="text-[9px]">✅ PAID IN FULL</span></span>
                  ) : (
                    <span className="font-bold">{formatMoney(0)}</span>
                  )}
                </div>
                {creditAmount > 0 && (
                  <div className="flex justify-between py-0.5">
                    <span className="font-bold text-gray-800">Credit <span className="font-normal text-gray-500">(overpaid)</span></span>
                    <span className="font-bold" style={{ color: '#0077B6' }}>{formatMoney(creditAmount)}</span>
                  </div>
                )}
              </>
            )
          )}
        </div>
      </div>

      {/* Notes */}
      {document.notes && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mb-3 print:hidden">
          <p className="text-xs font-bold text-gray-500">NOTES: </p>
          <p className="text-gray-700 text-xs">{document.notes}</p>
        </div>
      )}

      {/* Payment Record — compact, boxless. Visible in print for invoices with payments.
          One line per payment; totals below. Reads the same paymentHistory data. */}
      {type === 'invoice' && paymentHistory.length > 0 && (
        <div className="mb-3 text-[11px] leading-snug">
          <p className="font-bold text-gray-700 uppercase mb-0.5">Payment Record</p>
          {paymentHistory.map((pmt) => {
            const amt = parseFloat(pmt.amount || 0);
            const dateStr = pmt.date
              ? new Date(pmt.date + 'T12:00:00').toLocaleDateString('en-US')
              : new Date(pmt.createdAt).toLocaleDateString('en-US');
            return (
              <p key={pmt.id} className="text-gray-700">
                {dateStr} · {pmt.method || '—'} · <span className="font-bold text-green-700">{formatMoney(amt)}</span>
                {/* Cash-with-change only (exact cash adds no note). Reads stored
                    cashReceived/changeDue, so it prints on the saved doc/PDF. */}
                {pmt.method === 'Cash' && pmt.cashReceived != null && parseFloat(pmt.changeDue || 0) > 0 && (
                  <span className="text-gray-400 ml-1">(Cash {formatMoney(parseFloat(pmt.cashReceived))} · Change {formatMoney(parseFloat(pmt.changeDue))})</span>
                )}
                {isAdmin && (
                  <button
                    onClick={() => handleDeletePayment(pmt)}
                    title={`Delete this payment of ${formatMoney(amt)}`}
                    className="print:hidden ml-2 align-baseline"
                    style={{ background: 'none', border: 'none', color: '#A32D2D', cursor: 'pointer', fontSize: 12, lineHeight: 1, padding: 0 }}
                  >
                    🗑
                  </button>
                )}
              </p>
            );
          })}
        </div>
      )}

      {/* Customer signature ABOVE, then full-width Terms box below. */}
      <div style={{ breakInside: 'avoid' }}>
        {/* CUSTOMER SIGNATURE — one slot, the original footprint.
            The 24px box above the rule is the space someone signs in with a
            pen; when a signature was captured in the shop it is drawn in that
            same box. The block is the same height either way. */}
        <div className="mb-2">
          <div className="border-b-2 border-gray-400 mb-1 h-6" style={{ maxWidth: 320 }}>
            {hasSignature(document) && (
              <SignatureView path={document.pickupSignature} height={24} style={{ maxWidth: 320 }} />
            )}
          </div>
          <div className="flex items-center gap-6">
            <p className="text-xs text-gray-600">
              {hasSignature(document) ? document.pickupSignedName : 'CUSTOMER SIGNATURE'}
            </p>
            <p className="text-xs text-gray-400">
              {hasSignature(document)
                ? `Date: ${fmtSignedAt(document.pickupSignedAt)}`
                : 'Date: _______________'}
            </p>
            {hasSignature(document) && document.pickupWitnessedBy && (
              <p className="text-xs text-gray-400">Witnessed by {document.pickupWitnessedBy}</p>
            )}
          </div>
        </div>
        {/* Terms & Conditions — full width, soft-blue box, compact 2-column list.
            print-color-adjust:exact keeps the tint in the PDF (browsers drop
            backgrounds in print by default). */}
        <div className="rounded p-2 mb-2" style={{ backgroundColor: '#E6F1FB', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
          <p className="font-bold text-gray-700 text-[10px] mb-0.5">TERMS & CONDITIONS</p>
          <ol className="list-decimal list-inside space-y-0 columns-2 gap-6">
            {TERMS.map((term, index) => (
              <li key={index} className="text-[9px] text-gray-600 leading-snug break-inside-avoid">{term}</li>
            ))}
          </ol>
        </div>
      </div>
      <p className="text-center text-gray-300 mt-3" style={{ fontSize: '7px' }}>powered by Fence Flow</p>
    </>
  );

  return (
    <>
    <div className="min-h-screen vd-root" style={{ backgroundColor: '#CDE7F5', fontFamily: 'Manrope, sans-serif' }}>

      {/* Clone Modal */}
      {showCloneModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(3,4,94,0.5)' }}>
          <div className="rounded-2xl max-w-sm w-full p-6" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', boxShadow: '0 8px 24px rgba(3,4,94,0.15)' }}>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#03045E' }}>📋 Clone {type === 'quote' ? 'Quote' : wordTitle}</h3>
            <p className="text-sm mb-6" style={{ color: '#6B7C93' }}>
              A new document will be created with the same customer, line items, delivery details and notes. Comments, board status, P.O., payments and pick history are not copied. Clone as which type?
            </p>
            <div className="space-y-3">
              <button
                onClick={() => handleClone('quote')}
                disabled={cloning}
                className="w-full disabled:opacity-60 font-bold px-5 py-3 rounded-xl transition"
                style={{ backgroundColor: '#0077B6', color: '#FFFFFF' }}
              >
                {cloning ? 'Cloning...' : '📄 Clone as Quote'}
              </button>
              <button
                onClick={() => handleClone('invoice')}
                disabled={cloning}
                className="w-full disabled:opacity-60 font-bold px-5 py-3 rounded-xl transition"
                style={{ backgroundColor: '#0F6E56', color: '#FFFFFF' }}
              >
                {cloning ? 'Cloning...' : `🧾 Clone as ${wordTitle}`}
              </button>
              <button
                onClick={() => setShowCloneModal(false)}
                disabled={cloning}
                className="w-full disabled:opacity-60 font-bold px-5 py-3 rounded-xl transition"
                style={{ backgroundColor: '#E6F1FB', color: '#6B7C93' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(3,4,94,0.5)' }}>
          <div className="rounded-2xl max-w-md w-full p-6" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', boxShadow: '0 8px 24px rgba(3,4,94,0.15)' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold" style={{ color: '#03045E' }}>💳 Record Payment</h3>
              <button onClick={() => setShowPaymentModal(false)} className="text-xl font-bold" style={{ color: '#6B7C93' }}>✕</button>
            </div>

            {/* Balance summary */}
            <div className="rounded-xl p-4 mb-5 text-sm" style={{ backgroundColor: '#CDE7F5' }}>
              <div className="flex justify-between mb-1">
                <span style={{ color: '#6B7C93' }}>{wordTitle} Total</span>
                <span className="font-bold" style={{ color: '#03045E' }}>${grandTotal.toFixed(2)}</span>
              </div>
              {totalPaid > 0 && (
                <div className="flex justify-between mb-1">
                  <span style={{ color: '#6B7C93' }}>Previously Paid</span>
                  <span className="font-bold" style={{ color: '#0F6E56' }}>− ${totalPaid.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold pt-1 mt-1" style={{ borderTop: '1px solid #E3ECF5' }}>
                <span style={{ color: '#03045E' }}>Remaining Balance</span>
                <span style={{ color: '#0077B6' }}>${balanceDue.toFixed(2)}</span>
              </div>
            </div>

            <div className="space-y-4">
              {/* Cash takes "Cash Received" instead of "Amount Paid" — the recorded
                  payment is min(received, balanceDue), so a partial cash payment still
                  behaves exactly as before and any excess is shown as change to return. */}
              {isCash ? (
                <div>
                  <label className="block font-semibold text-sm mb-1" style={{ color: '#6B7C93' }}>Cash Received *</label>
                  <input
                    type="number" step="0.01"
                    value={paymentForm.cashReceived}
                    onChange={(e) => setPaymentForm(f => ({ ...f, cashReceived: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2 focus:outline-none text-lg font-bold"
                    style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                    onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}
                    placeholder={balanceDue.toFixed(2)}
                  />
                  {cashReceived > 0 && (
                    <p className="text-xs mt-1 font-semibold" style={{ color: recordedAmount >= balanceDue ? '#0F6E56' : '#854F0B' }}>
                      {recordedAmount >= balanceDue
                        ? '✅ Full Payment'
                        : `⚠️ Partial — $${Math.max(0, balanceDue - recordedAmount).toFixed(2)} still remaining`
                      }
                    </p>
                  )}
                  {changeDue >= 0.01 && (
                    <p className="text-sm mt-1 font-bold" style={{ color: '#0F6E56' }}>
                      💵 Change: ${changeDue.toFixed(2)}
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block font-semibold text-sm mb-1" style={{ color: '#6B7C93' }}>Amount Paid *</label>
                  <input
                    type="number" step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2 focus:outline-none text-lg font-bold"
                    style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                    onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}
                    placeholder={balanceDue.toFixed(2)}
                  />
                  {/* Not capped and not rejected: more than the balance is
                      recorded as entered and shows as a credit — said here,
                      before it is saved, so it is never a surprise. */}
                  {recordedAmount > 0 && (
                    <p className="text-xs mt-1 font-semibold" style={{ color: recordedAmount > balanceDue ? '#0077B6' : recordedAmount === balanceDue ? '#0F6E56' : '#854F0B' }}>
                      {recordedAmount > balanceDue
                        ? `💙 Overpayment — $${round2(recordedAmount - balanceDue).toFixed(2)} will show as a credit`
                        : recordedAmount === balanceDue
                          ? '✅ Full Payment'
                          : `⚠️ Partial — $${round2(balanceDue - recordedAmount).toFixed(2)} still remaining`
                      }
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block font-semibold text-sm mb-1" style={{ color: '#6B7C93' }}>Payment Method *</label>
                <select
                  value={paymentForm.method}
                  onChange={(e) => setPaymentForm(f => ({ ...f, method: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 focus:outline-none"
                  style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                  onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}
                >
                  <option value="Cash">💵 Cash</option>
                  <option value="Credit/Debit Card">💳 Credit/Debit Card</option>
                  <option value="Check">🏦 Check</option>
                  <option value="Other">🔄 Other</option>
                </select>
              </div>

              {paymentForm.method === 'Check' && (
                <div>
                  <label className="block font-semibold text-sm mb-1" style={{ color: '#6B7C93' }}>Check Number</label>
                  <input
                    type="text"
                    value={paymentForm.checkNumber}
                    onChange={(e) => setPaymentForm(f => ({ ...f, checkNumber: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2 focus:outline-none"
                    style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                    onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}
                    placeholder="e.g. 1234"
                  />
                </div>
              )}

              <div>
                <label className="block font-semibold text-sm mb-1" style={{ color: '#6B7C93' }}>Date *</label>
                <input
                  type="date"
                  value={paymentForm.date}
                  onChange={(e) => setPaymentForm(f => ({ ...f, date: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 focus:outline-none"
                  style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                  onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}
                />
              </div>

              <div>
                <label className="block font-semibold text-sm mb-1" style={{ color: '#6B7C93' }}>Notes</label>
                <input
                  type="text"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2 focus:outline-none"
                  style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
                  onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleRecordPayment}
                disabled={recordingPayment}
                className="flex-1 disabled:opacity-60 font-bold px-5 py-3 rounded-xl transition"
                style={{ backgroundColor: '#0F6E56', color: '#FFFFFF' }}
              >
                {recordingPayment ? 'Recording...' : '✅ Record Payment'}
              </button>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="font-bold px-5 py-3 rounded-xl transition"
                style={{ backgroundColor: '#E6F1FB', color: '#6B7C93' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Bar */}
      <div className="px-6 py-4 flex items-center justify-between print:hidden" style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E3ECF5' }}>
        <div className="flex items-center gap-3">
          <img src={require('../assets/fenceflow-logo.png')} alt="Fence Flow" className="w-10 h-10 object-contain rounded-lg" />
          <div>
            <h1 className="font-bold text-lg leading-tight" style={{ color: '#03045E' }}>Fence Flow</h1>
            <p className="text-xs" style={{ color: '#6B7C93' }}>
              {type === 'quote' ? 'Sale Quote' : wordTitle} #{document.docNumber}
            </p>
          </div>
          {/* Payment status badge in action bar for invoices */}
          {type === 'invoice' && !loading && (
            <div className="ml-4 flex items-center gap-3 flex-wrap">
              {paymentStatusBadge()}
              {/* ── INVENTORY STATUS — the one place the screen says it ──
                  This chip used to be a quiet pink "INVENTORY PENDING", with a
                  separate floating red box repeating the warning. That box was
                  position:fixed, pinned to the window's right edge while the
                  document is centred, so on anything narrower than ~1870px it sat
                  on top of the document and stayed there while scrolling.

                  Now the chip carries the warning itself: solid red with white
                  text when stock has not come off, the reminder in its tooltip,
                  and the Mark as Deducted button in the side column. It lives in
                  this bar, which is in the page flow — it can push the document
                  down but never cover it — and the bar is print:hidden, so it
                  never reaches paper. */}
              {(() => {
                const deducted = resolveInventoryStatus(document) === 'deducted';
                return deducted ? (
                  <span
                    data-inventory-status="deducted"
                    className="font-bold px-3 py-1 rounded-lg text-sm flex items-center gap-1 whitespace-nowrap"
                    style={{ backgroundColor: '#E8F5F0', color: '#0F6E56' }}
                    title="Stock for this invoice has been taken off inventory."
                  >
                    🟢 INVENTORY DEDUCTED
                  </span>
                ) : (
                  <span
                    data-inventory-status="pending"
                    role="status"
                    className="font-bold px-3 py-1 rounded-lg text-sm flex items-center gap-1 whitespace-nowrap"
                    style={{ backgroundColor: '#A32D2D', color: '#FFFFFF', boxShadow: '0 1px 4px rgba(163,45,45,0.35)' }}
                    title="Stock has not been taken off inventory for this invoice. Use Mark as Deducted when the materials leave the warehouse."
                  >
                    ⚠️ INVENTORY NOT DEDUCTED
                  </span>
                );
              })()}
              {isPickRequested(document) && (() => {
                const s = PICK_STATUS[resolvePickStatus(document)];
                return (
                  <span className="font-bold px-3 py-1 rounded-lg text-sm flex items-center gap-1"
                    style={{ backgroundColor: s.bg, color: s.fg }}>
                    {s.icon} {s.label.toUpperCase()}
                  </span>
                );
              })()}
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold whitespace-nowrap" style={{ color: '#6B7C93' }}>Board:</span>
                <select
                  value={document.status || DEFAULT_BOARD_STATUS}
                  onChange={e => updateBoardStatus(e.target.value)}
                  disabled={updatingStatus}
                  className="text-xs rounded-lg px-2 py-1.5 focus:outline-none cursor-pointer disabled:opacity-60"
                  style={{ backgroundColor: '#FFFFFF', color: '#03045E', border: '1px solid #E3ECF5' }}
                  onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}
                >
                  {BOARD_STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                {updatingStatus && <span className="text-xs" style={{ color: '#6B7C93' }}>Saving…</span>}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {type === 'quote' && (
            document.convertedToInvoice ? (
              <button
                onClick={handleConvertToInvoice}
                title={`Already converted — open invoice #${document.convertedToInvoice}`}
                className="text-sm font-bold px-4 py-2 rounded-lg transition"
                style={{ backgroundColor: '#E6F1FB', color: '#6B7C93', border: '1px solid #E3ECF5' }}
              >
                ✓ Converted → #{document.convertedToInvoice}
              </button>
            ) : (
              <button
                onClick={handleConvertToInvoice}
                disabled={converting}
                className="disabled:opacity-60 font-bold text-sm px-5 py-2 rounded-lg transition"
                style={{ backgroundColor: '#E6F7EE', color: '#0F6E56', border: '1px solid #0F6E56' }}
              >
                {converting ? 'Converting...' : `🧾 Convert to ${wordTitle}`}
              </button>
            )
          )}
        </div>
      </div>

      {/* Success banner */}
      {paymentSuccess && (
        <div className="px-6 py-3 font-semibold text-sm print:hidden" style={{ backgroundColor: '#E6F7EE', borderBottom: '1px solid #0F6E56', color: '#0F6E56' }}>
          {paymentSuccess}
        </div>
      )}

      {/* Credit hold — SCREEN ONLY (print:hidden).
          It is an internal instruction to whoever is serving this customer, not
          a term of this order: the customer's own copy should not carry the
          reason their credit was stopped, and a paid invoice printed months
          later would state a hold that may since have been lifted. It is read
          from the CUSTOMER record, live, never from the document. */}
      {liveCustomer?.creditHold && (
        <div className="px-6 pt-4 print:hidden">
          <CreditHoldWarning customer={liveCustomer} />
        </div>
      )}

      {/* The centred column — the document and every panel below it — in one
          wrapper, so the room reserved for the action column applies to all of
          them. See ACTIONS_LAYOUT_CSS. */}
      <div className="vd-content">
      {/* Document — multi-copy print stack OR single screen view */}
      {printMode === 'multi' ? (
        <div className="print:my-0">
          {Array.from({ length: printCopies }).map((_, i) => {
            const isLast = i === printCopies - 1;
            return (
              <div
                key={i}
                className="bg-white px-8 py-6"
                style={{ pageBreakAfter: i < printCopies - 1 ? 'always' : 'auto' }}
              >
                {isLast ? renderPackingSlipBody() : renderFullDocBody()}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="max-w-4xl mx-auto my-6 print:my-0 print:max-w-none">
          <div className="bg-white border border-[#E3ECF5] print:border-0 print:shadow-none px-8 py-6">
            {renderFullDocBody()}
          </div>
        </div>
      )}

      {/* ── COLLECTION TICKS — INVOICES SENT TO THE SHOP, ON SCREEN ONLY ──
          The counter's copy of the shop tablet's checklist, so a line handed
          over at the front desk is recorded in the same field the shop writes.
          Whatever is left unticked IS the back order — there is no separate
          back-order flag to fall out of step with it.

          It is deliberately NOT a column inside the document. The invoice the
          customer takes away is a record of what they bought, and a half-ticked
          column printed on it reads as a dispute about what they received. So
          the ticks live below the document and print:hidden keeps them off
          paper entirely.

          INVENTORY IS NOT TOUCHED BY ANY OF THIS. The stock came off when the
          invoice was marked deducted; ticking records that the customer has
          physically carried the goods out. See the warning in pickStatus.js. */}
      {type === 'invoice' && isPickRequested(document) && (
        <div className="print:hidden" style={{ maxWidth: 896, margin: '0 auto 32px', fontFamily: 'Manrope, sans-serif' }}>
          <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #E3ECF5', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: '#0B2545', letterSpacing: 0.3 }}>COLLECTED</span>
              {(() => {
                const total = collectableCount(document);
                const left = backOrderCount(document);
                if (total === 0) return <span style={{ fontSize: 13, color: '#5A6B80' }}>No items on this order</span>;
                if (left === 0) return <span style={{ fontSize: 13, fontWeight: 700, color: '#0B5B44' }}>All {total} items collected</span>;
                return (
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#8A3D00' }}>
                    {left} of {total} {left === 1 ? 'item' : 'items'} on back order
                  </span>
                );
              })()}
            </div>
            <div>
              {collectableItems(document).length === 0 ? (
                <p style={{ margin: 0, padding: '14px 16px', fontSize: 13, color: '#5A6B80' }}>
                  Nothing on this invoice can be collected.
                </p>
              ) : collectableItems(document).map((item, i) => {
                const picked = isItemPicked(item, document);
                const tickable = !!itemKey(item.id);
                return (
                  <div
                    key={item.id ?? `row-${i}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '8px 16px',
                      borderTop: i === 0 ? 'none' : '1px solid #F0F4F9',
                      background: picked ? '#F4FBF7' : '#FFFFFF',
                    }}
                  >
                    <button
                      type="button"
                      disabled={!tickable}
                      onClick={() => toggleCollected(item)}
                      aria-pressed={picked}
                      aria-label={(picked ? 'Collected: ' : 'Not collected: ') + (item.description || '')}
                      title={tickable ? (picked ? 'Mark as not collected' : 'Mark as collected') : 'This line has no id and cannot be ticked'}
                      style={{
                        width: 26, height: 26, flexShrink: 0, borderRadius: 6,
                        border: `2px solid ${picked ? '#0F6E56' : '#9FB3C8'}`,
                        background: picked ? '#0F6E56' : '#FFFFFF',
                        color: '#FFFFFF', fontSize: 15, lineHeight: 1, fontWeight: 900,
                        cursor: tickable ? 'pointer' : 'not-allowed',
                        opacity: tickable ? 1 : 0.4,
                      }}
                    >
                      {picked ? '✓' : ''}
                    </button>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#0B2545', minWidth: 54 }}>
                      {item.qty} {item.unit}
                    </span>
                    <span style={{ fontSize: 13, color: picked ? '#5A6B80' : '#0B2545', flex: 1, minWidth: 0, textDecoration: picked ? 'line-through' : 'none' }}>
                      {item.description}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── STEP THROUGH THE LIST ── ON SCREEN ONLY ──
          Only when the user arrived from a list. Nothing here appears on a
          document opened from the Documents page or a link.

          DISABLED AT THE ENDS, NEVER HIDDEN. A control that disappears at the
          last document reads as something breaking — the person looks for the
          button they were just using and it is gone. Greyed out says "you are
          at the end", which is the actual fact. */}
      {showDocNav && (
        <div className="print:hidden" style={{ maxWidth: 896, margin: '0 auto 16px', fontFamily: 'Manrope, sans-serif' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 12,
            padding: '10px 14px',
          }}>
            <button
              onClick={backToList}
              style={{
                background: 'transparent', border: '1px solid #E3ECF5', borderRadius: 8,
                padding: '6px 12px', fontSize: 13, fontWeight: 700, color: '#5A6B80',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              ← Back to customer
            </button>

            <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700, color: '#0B2545' }}>
              {navPos + 1} of {navIds.length}
              <span style={{ fontWeight: 500, color: '#5A6B80' }}>
                {' '}{docNav.type === 'quote' ? 'quotes' : 'invoices'}
              </span>
            </span>

            <button
              onClick={() => stepTo(navPos - 1)}
              disabled={!hasPrev}
              aria-label="Previous document"
              style={navStepStyle(!hasPrev)}
            >
              ← Previous
            </button>
            <button
              onClick={() => stepTo(navPos + 1)}
              disabled={!hasNext}
              aria-label="Next document"
              style={navStepStyle(!hasNext)}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* ── LINKED SHOP DRAWINGS — ON SCREEN ONLY ──
          Which gate drawings were made for this document. READ ONLY: the link
          lives on the drawing, because the drawing is what knows which document
          it belongs to, and one owner for one fact is what stops the two sides
          disagreeing. To change it, open the drawing.

          print:hidden — a customer's copy is a record of what they bought, not
          of the shop paperwork behind it. */}
      {linkedDrawings.length > 0 && (
        <div className="print:hidden" style={{ maxWidth: 896, margin: '0 auto 32px', fontFamily: 'Manrope, sans-serif' }}>
          <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 12, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: '#0B2545', letterSpacing: 0.3 }}>
                SHOP DRAWINGS
              </span>
              <span style={{ fontSize: 12, color: '#5A6B80' }}>
                {linkedDrawings.length} linked
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {linkedDrawings.map(d => (
                <button
                  key={d.id}
                  onClick={() => navigate('/shop-drawings', { state: { drawingId: d.id } })}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                    textAlign: 'left', width: '100%',
                    background: '#F7FAFD', border: '1px solid #E3ECF5',
                    borderRadius: 10, padding: '9px 12px',
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#5B21B6', fontSize: 13 }}>
                    {d.drawingNumber || '—'}
                  </span>
                  <span style={{ color: '#0B2545', fontSize: 13 }}>{d.type || '—'}</span>
                  <span style={{ color: '#5A6B80', fontSize: 13 }}>
                    {drawingSize(d.openingIn)} × {drawingSize(d.heightIn)}
                  </span>
                  {(d.quantity || 1) > 1 && (
                    <span style={{
                      background: '#03045E', color: '#FFFFFF', fontSize: 11, fontWeight: 800,
                      padding: '1px 7px', borderRadius: 6,
                    }}>
                      QTY {d.quantity}
                    </span>
                  )}
                  <span style={{ color: '#9AA8BC', fontSize: 12, marginLeft: 'auto' }}>Open →</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── PICKUP SIGNATURE — INVOICES, ON SCREEN, READ ONLY ──
          The office SEES the signature; it does not take one.
          
          Capture belongs on the shop tablet and nowhere else, because that is
          where the customer is physically standing when they collect the
          material. A pad on a desktop in the office is a pad somebody signs on
          behalf of a customer who is not in the room, which is worth less than
          no signature at all.

          Removing capture does NOT remove the record: a signature taken in the
          shop still shows here in full, with the name, the date and who
          witnessed it, and still prints on the invoice and the packing slip. */}
      {type === 'invoice' && (
        <div className="print:hidden" style={{ maxWidth: 896, margin: '0 auto 32px', fontFamily: 'Manrope, sans-serif' }}>
          <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 12, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: '#0B2545', letterSpacing: 0.3 }}>
                PICKUP SIGNATURE
              </span>
              {hasSignature(document) && (
                <span style={{ fontSize: 13, fontWeight: 700, color: '#0B5B44' }}>✓ Signed</span>
              )}
            </div>

            {hasSignature(document) ? (
              <div style={{ border: '2px solid #E3ECF5', borderRadius: 12, background: '#FFFFFF', padding: 12 }}>
                <SignatureView path={document.pickupSignature} height={90} />
                <div style={{ borderTop: '1px solid #E3ECF5', marginTop: 6, paddingTop: 6 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#03045E' }}>
                    {document.pickupSignedName}
                  </p>
                  <p style={{ margin: '2px 0 0 0', fontSize: 12, color: '#54657A' }}>
                    {fmtSignedAt(document.pickupSignedAt)}
                    {document.pickupWitnessedBy ? ` · witnessed by ${document.pickupWitnessedBy}` : ''}
                  </p>
                </div>
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: 13, color: '#5A6B80' }}>
                Not signed yet. Signatures are taken on the shop tablet when the customer
                collects; the printed copy carries a blank line for a pen.
              </p>
            )}
          </div>
        </div>
      )}
      </div>

      {/* Right-side action column — positioned by ACTIONS_LAYOUT_CSS */}
      {/* Robust print-hide: position:fixed can escape Tailwind's print:hidden in
          some browsers, so force display:none in print via a dedicated class. */}
      <style>{`@media print { .screen-only-actions { display: none !important; } }`}</style>
      <style>{PRINT_BADGE_CSS}</style>
      <style>{ACTIONS_LAYOUT_CSS}</style>

      {/* The floating "INVENTORY NOT DEDUCTED" box that used to sit here is gone.
          It was position:fixed at right:226 beside the button column, while the
          document is a centred column — so below ~1870px of window width it
          covered the document. Its message now lives in the inventory chip in
          the action bar at the top of the page. */}

      <aside className="screen-only-actions print:hidden vd-actions">
        {/* ── EDIT ──────────────────────────────────────────────────
            FIRST in the sidebar, and filled rather than outlined.

            This control already existed, but it was written for somebody who
            had arrived FROM the editor: it sat near the bottom between Cancel
            Request and Delete, wore the same quiet white-and-grey as the
            secondary actions, and read "← Back to Edit". That was accurate
            when the only way here was the editor's own View button. It is not
            accurate now — the customer profile's Quotes and Invoices tabs open
            this page directly, so a person who came to EDIT has never been in
            the editor and "back" means nothing to them. They would hunt.

            So: named for what it does, given the group's primary treatment, and
            moved above Print, which is the other thing people come here for.

            PERMISSIONS ARE UNCHANGED — canEdit, the same flag that gated the
            old button: an admin, or the salesperson whose name is on the
            document. Deliberately not narrowed to admins: doing so would take
            away access a salesperson has today, which is a regression dressed
            up as a permission fix. */}
        {canEdit && (
          <>
            <p style={{ color: '#6B7C93', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', margin: '0 0 -2px 2px' }}>Edit</p>
            <button
              onClick={() => navigate(`/edit-document/${type}/${id}`)}
              title={`Open this ${type === 'quote' ? 'quote' : 'invoice'} in the editor`}
              style={{ width: '100%', background: '#0077B6', border: 'none', color: '#fff', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              ✏️ Edit {type === 'quote' ? 'Quote' : 'Invoice'}
            </button>
          </>
        )}

        {/* PRINT group — type-aware (invoices get full set; quotes get single print) */}
        <p style={{ color: '#6B7C93', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', margin: '0 0 -2px 2px' }}>Print</p>
        {type === 'invoice' ? (
          <>
            <button
              onClick={handleMultiPrint}
              title="Prints 2 priced copies + 1 packing slip"
              style={{ width: '100%', background: '#0077B6', border: 'none', color: '#fff', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              🖨️ Print 3 Copies
            </button>
            <button
              onClick={handlePrint}
              title="Print only this single copy"
              style={{ width: '100%', background: '#E6F1FB', border: '1px solid #0077B6', color: '#0077B6', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              📄 Print 1 Copy
            </button>
            <button
              onClick={handlePrintPackingSlip}
              style={{ width: '100%', background: '#E0F7FA', border: '1px solid #00B4D8', color: '#0096C7', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              📦 Print Packing Slip
            </button>
          </>
        ) : (
          <button
            onClick={handlePrint}
            title="Print a single copy"
            style={{ width: '100%', background: '#0077B6', border: 'none', color: '#fff', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            🖨️ Print / PDF
          </button>
        )}

        {/* ACTIONS group */}
        <p style={{ color: '#6B7C93', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', margin: '8px 0 -2px 2px' }}>Actions</p>
        <button
          onClick={() => navigate('/dashboard')}
          style={{ width: '100%', background: '#7C3AED', border: 'none', color: '#fff', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          ← Dashboard
        </button>
        {/* Offered whenever money is OWED, computed live — so an invoice paid in
            full and then given a delivery fee offers it again. Hidden while the
            payment records are unreadable: the modal's balance would be a guess. */}
        {type === 'invoice' && paymentsKnown && balanceDue > 0 && (
          <button
            onClick={openPaymentModal}
            style={{ width: '100%', background: '#E6F7EE', border: '1px solid #0F6E56', color: '#0F6E56', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            💳 Record Payment
          </button>
        )}
        {type === 'invoice' && (
          resolveInventoryStatus(document) === 'pending' ? (
            <button
              onClick={markAsDeducted}
              disabled={inventoryBusy}
              title="Deduct this invoice's stock-tracked items from inventory"
              style={{ width: '100%', background: '#0077B6', border: 'none', color: '#fff', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: inventoryBusy ? 'not-allowed' : 'pointer', opacity: inventoryBusy ? 0.6 : 1, boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              {inventoryBusy ? 'Working…' : '📦 Mark as Deducted'}
            </button>
          ) : (
            <button
              onClick={revertToPending}
              disabled={inventoryBusy}
              title="Return this invoice's items to stock and mark it pending"
              style={{ width: '100%', background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#6B7C93', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: inventoryBusy ? 'not-allowed' : 'pointer', opacity: inventoryBusy ? 0.6 : 1, boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              {inventoryBusy ? 'Working…' : '↩️ Revert to Pending'}
            </button>
          )
        )}
        <button
          onClick={() => setShowCloneModal(true)}
          style={{ width: '100%', background: '#E6F1FB', border: '1px solid #023E8A', color: '#023E8A', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          📋 Clone
        </button>
        {/* ── SHOP ──
            Invoices only, admins only. A shop user never renders this aside at
            all (the route guard sends them to /shop), but the isAdmin gate
            means the control cannot appear even if that changed. */}
        {type === 'invoice' && isAdmin && (
          <>
            <p style={{ color: '#6B7C93', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', margin: '6px 0 -2px 2px' }}>Shop</p>
            {!isPickRequested(document) ? (
              <button
                onClick={sendToShop}
                disabled={pickBusy}
                title="Put this invoice on the shop's picking list"
                style={{ width: '100%', background: '#FFF3DC', border: '1px solid #B26A00', color: '#7A4E00', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: pickBusy ? 'not-allowed' : 'pointer', opacity: pickBusy ? 0.6 : 1, boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                {pickBusy ? 'Working…' : '🛒 Send to Shop'}
              </button>
            ) : (
              /* Already queued. The send button is REPLACED, not disabled — an
                 invoice must not be able to enter the shop's list twice, and a
                 greyed-out button still reads as "try again". */
              (() => {
                const status = resolvePickStatus(document);
                const s = PICK_STATUS[status];
                const ready = status === 'Ready';
                return (
                  <>
                    <div style={{ width: '100%', background: s.bg, border: `1px solid ${s.fg}`, borderRadius: 8, padding: '9px 12px', boxSizing: 'border-box' }}>
                      <p style={{ color: s.fg, fontSize: 13, fontWeight: 700, margin: 0 }}>
                        {s.icon} {s.label}
                      </p>
                      <p style={{ color: '#6B7C93', fontSize: 11, margin: '3px 0 0 0', lineHeight: 1.4 }}>
                        {ready
                          ? `Picked by ${document.pickStatusBy || '—'}${document.pickStatusAt ? ' · ' + new Date(document.pickStatusAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}`
                          : `Sent by ${document.pickRequestedBy || '—'}${document.pickRequestedAt ? ' · ' + new Date(document.pickRequestedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}`}
                      </p>
                    </div>
                    {/* Cancelling is offered only while the shop has NOT
                        finished. Withdrawing something already picked would
                        tell the warehouse nothing and lose the record of who
                        picked it. */}
                    {!ready && (
                      <button
                        onClick={cancelPickRequest}
                        disabled={pickBusy}
                        title="Take this back off the shop's picking list"
                        style={{ width: '100%', background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#6B7C93', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: pickBusy ? 'not-allowed' : 'pointer', opacity: pickBusy ? 0.6 : 1, boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                      >
                        {pickBusy ? 'Working…' : '↩️ Cancel Request'}
                      </button>
                    )}
                  </>
                );
              })()
            )}

            {/* ── TAG MADE ──
                A checkbox row, not a button: it is a small operational fact,
                and giving it a button's weight would put it level with Print
                and Record Payment. It sits in the Shop group because that is
                where the office's warehouse-facing facts about an invoice
                already live — who sent it to be picked, and when — and TM is
                the same kind of fact: "the tag for this order exists".

                Inside this group's `type === 'invoice' && isAdmin` gate, so
                quotes, salespeople and the shop never see it. Who and when show
                quietly under it once ticked. */}
            <label
              title="Tick once the gate tag (shop drawing) or plated posts are made. The packing slip's Tag made box then prints already marked."
              style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '4px 2px 0 2px', cursor: tagBusy ? 'wait' : 'pointer' }}
            >
              <input
                type="checkbox"
                checked={tagMade}
                disabled={tagBusy}
                onChange={e => toggleTagMade(e.target.checked)}
                style={{ width: 15, height: 15, marginTop: 2, accentColor: '#0077B6', flexShrink: 0 }}
              />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', color: '#03045E', fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>
                  Tag made (TM)
                </span>
                {tagMade && (
                  <span style={{ display: 'block', color: '#6B7C93', fontSize: 11, lineHeight: 1.4 }}>
                    {document.tagMadeBy || '—'}
                    {document.tagMadeAt && !Number.isNaN(new Date(document.tagMadeAt).getTime())
                      ? ' · ' + new Date(document.tagMadeAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                      : ''}
                  </span>
                )}
              </span>
            </label>
          </>
        )}
        {isAdmin && (
          <button
            onClick={() => initiateDelete(document)}
            disabled={deleting}
            style={{ width: '100%', background: '#FBECEC', border: '1px solid #A32D2D', color: '#A32D2D', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.6 : 1, boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            {deleting ? 'Deleting…' : '🗑️ Delete'}
          </button>
        )}
      </aside>
    </div>

    {/* Delete confirmation modal */}
    {showDeleteModal && docToDelete && (
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(3,4,94,0.5)' }}>
        <div className="rounded-xl p-6 max-w-md w-full" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', boxShadow: '0 8px 24px rgba(3,4,94,0.15)' }}>
          <h3 className="text-xl font-bold mb-2" style={{ color: '#A32D2D' }}>⚠️ Delete Document</h3>
          <p className="mb-4" style={{ color: '#6B7C93' }}>
            You are about to permanently delete{' '}
            <strong>{docToDelete.docType === 'quote' ? 'Quote' : wordTitle} #{docToDelete.docNumber}</strong>
            {' '}for {docToDelete.customerName}. This cannot be undone.
          </p>
          <p className="font-semibold mb-2" style={{ color: '#03045E' }}>Enter your password to confirm:</p>
          <input
            type="password"
            value={deletePassword}
            onChange={e => setDeletePassword(e.target.value)}
            placeholder="Your login password"
            className="w-full rounded-lg px-4 py-2 mb-2 focus:outline-none"
            style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E' }}
            onFocus={e => e.target.style.borderColor = '#0077B6'} onBlur={e => e.target.style.borderColor = '#E3ECF5'}
            onKeyDown={e => { if (e.key === 'Enter' && deletePassword) confirmDelete(); }}
            autoFocus
          />
          {deleteError && <p className="text-sm mb-2" style={{ color: '#A32D2D' }}>{deleteError}</p>}
          <div className="flex gap-3 mt-4">
            <button
              onClick={confirmDelete}
              disabled={deleting || !deletePassword}
              className="disabled:opacity-50 font-bold px-6 py-2 rounded-lg flex-1 transition"
              style={{ backgroundColor: '#A32D2D', color: '#FFFFFF' }}
            >
              {deleting ? 'Deleting…' : 'Delete Permanently'}
            </button>
            <button
              onClick={() => { setShowDeleteModal(false); setDeletePassword(''); setDeleteError(''); }}
              className="font-bold px-6 py-2 rounded-lg flex-1 transition"
              style={{ backgroundColor: '#E6F1FB', color: '#6B7C93' }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export default ViewDocument;
